import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Smile, X, Clock, BellOff, Eye, ImagePlus, Mic, Square, Sticker as StickerIcon } from 'lucide-react';
import { SwipaSend } from '@/icons/SwipaSend';
import clsx from 'clsx';
import { getSocket } from '@/lib/socket';
import { useTypingEmitter } from '@/hooks/useSocket';
import EmojiPicker from '@/components/ui/EmojiPicker';
import StickerPicker from './StickerPicker';
import { Message } from '@/types';
import { messagesApi } from '@/api/messages';
import { draftsApi } from '@/api/drafts';
import { mediaApi, UploadedMedia } from '@/api/media';
import { useChatStore } from '@/store/chatStore';
import ScheduleModal from './ScheduleModal';
import { outbox } from '@/lib/outbox';

/**
 * Phase 7 — клиентский ключ идемпотентности.
 *
 * Каждое нажатие "Send" получает свой UUID. Сервер хранит уникальный
 * индекс `(authorId, clientMessageId)` (и аналогично для DM), поэтому
 * повторная отправка из-за ретрая или дубля сокетного эмита не создаёт
 * второе сообщение. Тот же ключ используется на клиенте, чтобы дедупить
 * приходящее по сокету эхо собственного сообщения.
 */
function newClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Фолбэк для древних окружений / тестов.
  return `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

interface MessageInputProps {
  channelId?: string;
  recipientId?: string;
  placeholder?: string;
  disabled?: boolean;
  replyTo?: Message | null;
  onCancelReply?: () => void;
  mediaEnabled?: boolean;
  chatStyle?: 'BUBBLE' | 'CLASSIC';
  /**
   * v4.3 — для embed/мобильных layout'ов: emoji/stickers/attach кнопки
   * рендерятся отдельной row СВЕРХУ, textarea + send — отдельной row снизу.
   * По дефолту false (классический inline-layout как в desktop SWIPA).
   */
  stackInput?: boolean;
  /**
   * v5.2 — slow-mode countdown в композере. >0 = в канале включён slow-mode
   * со cooldown'ом N секунд. После каждого успешного send показывается
   * таймер в placeholder/кнопке, и кнопка disable'ится до истечения.
   * Server всё равно валидирует (single source of truth) — UI это лишь
   * подсказка, чтобы юзер не получал 429.
   */
  slowModeSeconds?: number;
}

export default function MessageInput({
  channelId,
  recipientId,
  placeholder,
  disabled,
  replyTo,
  onCancelReply,
  mediaEnabled = true,
  chatStyle = 'BUBBLE',
  stackInput = false,
  slowModeSeconds = 0,
}: MessageInputProps) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  // v5.2 — slow-mode UI countdown. Bump'ается на send-success и тикает вниз.
  // Per-scope (channel/DM) хранения нет — refresh страницы сбрасывает (правильно:
  // сервер всё равно знает реальную lastMessageAt, и при попытке слишком быстрого
  // send вернёт 429).
  const [slowUntil, setSlowUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!slowUntil) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [slowUntil]);
  const slowSecLeft = slowUntil ? Math.max(0, Math.ceil((slowUntil - now) / 1000)) : 0;
  const slowActive = slowSecLeft > 0;
  const [showEmoji, setShowEmoji] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [silent, setSilent] = useState(false);
  const [viewOnce, setViewOnce] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [attachedImage, setAttachedImage] = useState<{ file: File; preview: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const { startTyping, stopTyping } = useTypingEmitter(channelId ?? null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scope = channelId ? { channelId } : recipientId ? { peerUserId: recipientId } : null;
  const scopeKey = channelId ?? recipientId ?? '';
  const isClassic = chatStyle === 'CLASSIC';

  // Phase 20.1 — IRC classic: pick up @mention injection from store.
  const pendingMentionInject = useChatStore((s) => s.pendingMentionInject);
  const clearMentionInject = useChatStore((s) => s.clearMentionInject);
  useEffect(() => {
    if (!pendingMentionInject) return;
    if (pendingMentionInject.scopeKey !== scopeKey) return;
    setContent((prev) => {
      const needsSpace = prev.length > 0 && !prev.endsWith(' ') && !prev.endsWith('\n');
      return prev + (needsSpace ? ' ' : '') + pendingMentionInject.text;
    });
    const el = textareaRef.current;
    if (el) {
      setTimeout(() => {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 200) + 'px';
      }, 0);
    }
    clearMentionInject();
  }, [pendingMentionInject, scopeKey, clearMentionInject]);

  // Classic channels are text-only: drop any pending image attachment.
  useEffect(() => {
    if (isClassic && attachedImage) {
      setAttachedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isClassic, attachedImage]);

  // Load draft on mount / scope change
  useEffect(() => {
    if (!scope) { setDraftLoaded(true); return; }
    let cancelled = false;
    setDraftLoaded(false);
    (async () => {
      try {
        const drafts = await draftsApi.list();
        if (cancelled) return;
        const match = drafts.find((d) =>
          channelId ? d.channelId === channelId : d.peerUserId === recipientId,
        );
        if (match && match.content) {
          setContent(match.content);
          setTimeout(() => {
            const el = textareaRef.current;
            if (el) {
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 200) + 'px';
            }
          }, 0);
        } else {
          setContent('');
        }
      } catch { /* ignore */ }
      finally {
        if (!cancelled) setDraftLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Debounced draft save
  useEffect(() => {
    if (!draftLoaded || !scope) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      draftsApi.upsert({ ...scope, content }).catch(() => undefined);
    }, 600);
    return () => {
      if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, scopeKey, draftLoaded]);

  const sendSticker = async (url: string) => {
    if (!url || disabled) return;
    const cmid = newClientMessageId();
    useChatStore.getState().markPendingClientMessageId(cmid);
    try {
      if (channelId) {
        await messagesApi.sendChannelMessage(channelId, {
          content: url,
          type: 'STICKER',
          clientMessageId: cmid,
        });
      } else if (recipientId) {
        await messagesApi.sendDM(recipientId, {
          content: url,
          type: 'STICKER',
          clientMessageId: cmid,
        });
      }
    } catch (err) {
      useChatStore.getState().clearPendingClientMessageId(cmid);
      console.warn('[sticker] send failed', err);
    }
  };

  const send = async () => {
    const trimmed = content.trim();
    const hasImage = !!attachedImage;
    if ((!trimmed && !hasImage) || sending || disabled) return;

    // v5.4 — slash command interception. Если строка начинается с "/", даём
    // парсеру шанс выполнить команду локально (или превратить во "* action"
    // text). Парсер возвращает null если не slash — тогда обычный send-flow.
    if (trimmed.startsWith('/') && !hasImage) {
      const { parseSlash } = await import('@/lib/slashCommands');
      const result = await parseSlash(trimmed, {
        channelId,
        recipientId,
        // myRole не известен здесь без props; server всё равно 403'ит при
        // нехватке прав. Парсер увидит null и пропустит client-side guard.
        myRole: null,
        notify: (m) => alert(m), // TODO: заменить на toast-store
      });
      if (result?.kind === 'handled') {
        setContent('');
        return;
      }
      if (result?.kind === 'error') {
        alert(result.message);
        return;
      }
      if (result?.kind === 'send-text') {
        // Mutate text and continue normal send flow с заменённым контентом.
        setContent(result.content);
        // re-enter send next tick с обновлённым content. Проще: перезаписываем
        // локальную переменную и не выходим.
        // (state update в React async, поэтому используем local override.)
        // Лёгкий путь — просто продолжить с result.content.
        // ↓ не выходим, дальше работаем с result.content
        const overrideContent = result.content;
        return await sendWithContent(overrideContent, hasImage);
      }
      // null → обычный send-flow с trimmed
    }
    return await sendWithContent(trimmed, hasImage);
  };

  // Внутренний хелпер: выполнить отправку с уже подготовленным content.
  // Выделено чтобы slash-command path не дублировал тело.
  const sendWithContent = async (textContent: string, hasImage: boolean) => {
    const socket = getSocket();
    setSending(true);
    const cmid = newClientMessageId();
    const markPending = useChatStore.getState().markPendingClientMessageId;
    const clearPending = useChatStore.getState().clearPendingClientMessageId;
    markPending(cmid);
    const trimmed = textContent;
    try {
      // Upload image first if attached
      let uploaded: UploadedMedia | undefined;
      if (attachedImage) {
        setUploading(true);
        uploaded = await mediaApi.upload(attachedImage.file, { channelId, recipientId });
        setUploading(false);
      }

      const useRest = silent || viewOnce || !!scheduledFor || !!uploaded;
      // v5.7.6 — watchdog для socket-path. socket.emit fire-and-forget,
      // если socket disconnected (например из-за expired token) — event
      // молча буферизуется, server echo `message:new` не приходит,
      // юзер видит пустой input и потерянное сообщение.
      // Watchdog ждёт WATCHDOG_MS, если за это время cmid всё ещё в
      // pendingClientMessageIds (echo не пришёл) — restore content
      // в input или draft + outbox + toast.
      // Per /codex msg 15fr37: restore делать аккуратно — только если
      // input всё ещё пустой (юзер не начал писать новое); иначе уйти
      // в outbox чтобы не затереть свежий ввод.
      const WATCHDOG_MS = 5000;
      let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
      const armSocketWatchdog = () => {
        watchdogTimer = setTimeout(() => {
          const stillPending = useChatStore.getState().pendingClientMessageIds.has(cmid);
          if (!stillPending) return; // echo пришёл — всё ок
          clearPending(cmid);
          // Восстанавливаем UX: если поле всё ещё пустое — возвращаем текст
          // в input. Если юзер уже начал писать новое — кладём в outbox
          // (не затирая ввод).
          const inputIsEmpty = textareaRef.current?.value === '' || textareaRef.current?.value === undefined;
          if (inputIsEmpty) {
            setContent(trimmed);
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto';
              textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
            }
          } else {
            // Outbox-fallback (как в network-error path).
            void outbox.put({
              clientMessageId: cmid,
              channelId,
              recipientId,
              content: trimmed,
              type: 'TEXT',
              replyToId: replyTo?.id,
              createdAt: Date.now(),
              retryCount: 0,
            }).catch((qErr) => console.warn('outbox.put (watchdog) failed', qErr));
          }
          console.warn('[message] socket emit watchdog timeout, no echo for', cmid);
        }, WATCHDOG_MS);
      };

      if (channelId) {
        if (useRest) {
          await messagesApi.sendChannelMessage(channelId, {
            content: trimmed || (uploaded ? '' : ''),
            type: uploaded ? 'IMAGE' : undefined,
            replyToId: replyTo?.id,
            silent,
            viewOnce,
            scheduledFor: scheduledFor ?? undefined,
            clientMessageId: cmid,
            ...(uploaded && { mediaFileId: uploaded.id }),
          });
        } else {
          socket.emit('message:send', { channelId, content: trimmed, replyToId: replyTo?.id, clientMessageId: cmid });
          armSocketWatchdog();
        }
      } else if (recipientId) {
        if (uploaded) {
          // DM with image uses REST
          await messagesApi.sendDM(recipientId, {
            content: trimmed || '',
            type: 'IMAGE',
            clientMessageId: cmid,
            replyToId: replyTo?.id,
            mediaFileId: uploaded.id,
          });
        } else {
          socket.emit('dm:send', { recipientId, content: trimmed, clientMessageId: cmid, replyToId: replyTo?.id });
          armSocketWatchdog();
        }
      }
      // Очищаем watchdog timer на размонтирование (через void: timer без
      // ref — закрытие watchdog'а сам себя deactivates после WATCHDOG_MS,
      // больше нечего clean'ать).
      void watchdogTimer;
      setContent('');
      setAttachedImage(null);
      setSilent(false);
      setViewOnce(false);
      setScheduledFor(null);
      onCancelReply?.();
      stopTyping();
      if (scope) draftsApi.clear(scope).catch(() => undefined);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // v5.2 — start slow-mode UI countdown if applicable. Channel-only;
      // в DM нет slow-mode. Не для bots/silent (silent path не gated на сервере).
      if (channelId && slowModeSeconds > 0 && !silent) {
        setSlowUntil(Date.now() + slowModeSeconds * 1000);
        setNow(Date.now());
      }
    } catch (err) {
      clearPending(cmid);
      setUploading(false);
      console.error('Send failed:', err);
      // Phase 11.4 — queue plain text for offline retry. We don't queue media
      // sends because they need an upload that may not survive a reconnect.
      const isMediaSend = !!attachedImage;
      const network = !navigator.onLine || (err as { code?: string })?.code === 'ERR_NETWORK';
      if (network && !isMediaSend && trimmed) {
        try {
          await outbox.put({
            clientMessageId: cmid,
            channelId,
            recipientId,
            content: trimmed,
            type: 'TEXT',
            replyToId: replyTo?.id,
            createdAt: Date.now(),
            retryCount: 0,
          });
          setContent('');
          setAttachedImage(null);
          onCancelReply?.();
        } catch (qErr) {
          console.warn('outbox.put failed', qErr);
        }
      }
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const preview = URL.createObjectURL(file);
    setAttachedImage({ file, preview });
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeAttachedImage = () => {
    if (attachedImage) {
      URL.revokeObjectURL(attachedImage.preview);
      setAttachedImage(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    startTyping();
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (el) {
      const start = el.selectionStart ?? content.length;
      const end = el.selectionEnd ?? content.length;
      const newContent = content.slice(0, start) + emoji + content.slice(end);
      setContent(newContent);
      setTimeout(() => {
        el.selectionStart = el.selectionEnd = start + emoji.length;
        el.focus();
      }, 0);
    } else {
      setContent((c) => c + emoji);
    }
    setShowEmoji(false);
  };

  const ph = placeholder ?? (channelId ? `Сообщение в #${channelId}` : 'Написать сообщение…');

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  return (
    <div className="px-3 py-2 md:px-4 md:py-3 border-t border-surface-800 bg-surface-950 flex-shrink-0 relative">
      {replyTo && (
        <div className="flex items-center gap-2 mb-1 px-3 py-1.5 bg-surface-800/50 rounded-t-xl border border-b-0 border-surface-700 text-xs">
          <div className="flex-1 min-w-0 border-l-2 border-brand-500 pl-2">
            <span className="text-brand-400 font-semibold">{replyTo.author.displayName}</span>
            <p className="text-surface-400 truncate">{replyTo.content}</p>
          </div>
          <button onClick={onCancelReply} className="p-1 text-surface-500 hover:text-surface-300">
            <X size={14} />
          </button>
        </div>
      )}

      {attachedImage && (
        <div className="flex items-center gap-2 mb-1 px-3 py-1.5 bg-surface-800/50 rounded-t-xl border border-b-0 border-surface-700">
          <img src={attachedImage.preview} alt="Вложение" className="w-12 h-12 rounded-lg object-cover" />
          <span className="text-xs text-surface-300 truncate flex-1">{attachedImage.file.name}</span>
          {uploading && <span className="text-xs text-brand-400 animate-pulse">Загрузка…</span>}
          <button onClick={removeAttachedImage} className="p-1 text-surface-500 hover:text-surface-300">
            <X size={14} />
          </button>
        </div>
      )}

      {scheduledFor && (
        <div className="flex items-center gap-2 mb-1 px-3 py-1 bg-brand-900/20 border border-brand-700/30 rounded-t-xl text-xs text-brand-300">
          <Clock size={12} />
          Отправка: {new Date(scheduledFor).toLocaleString('ru-RU')}
          <button onClick={() => setScheduledFor(null)} className="ml-auto p-1 text-brand-300 hover:text-white">
            <X size={12} />
          </button>
        </div>
      )}

      <div
        className={clsx(
          'bg-surface-800 rounded-xl border border-surface-700 focus-within:border-brand-500 transition-colors px-3 py-2',
          stackInput ? 'flex flex-col gap-1.5' : 'flex items-end gap-2',
        )}
      >
        {/* Top row of input buttons (или inline в classic mode через contents) */}
        <div className={clsx(stackInput ? 'flex items-center gap-2 flex-shrink-0' : 'contents')}>
          <button
            type="button"
            onClick={() => setShowEmoji((v) => !v)}
            disabled={disabled}
            className="flex-shrink-0 self-end p-1 rounded-lg text-surface-400 hover:text-brand-400 hover:bg-surface-700 transition-colors disabled:opacity-40 mb-0.5"
            title="Эмодзи"
          >
            <Smile size={18} />
          </button>

          {!isClassic && (
            <button
              type="button"
              onClick={() => setShowStickers((v) => !v)}
              disabled={disabled}
              className="flex-shrink-0 self-end p-1 rounded-lg text-surface-400 hover:text-brand-400 hover:bg-surface-700 transition-colors disabled:opacity-40 mb-0.5"
              title="Стикеры"
            >
              <StickerIcon size={18} />
            </button>
          )}

          {!isClassic && mediaEnabled && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || uploading}
                className="flex-shrink-0 self-end p-1 rounded-lg text-surface-400 hover:text-brand-400 hover:bg-surface-700 transition-colors disabled:opacity-40 mb-0.5"
                title="Прикрепить изображение"
              >
                <ImagePlus size={18} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageSelect}
                className="hidden"
              />
            </>
          )}
        </div>

        {/* Textarea + trailing buttons (eye/clock/send) — отдельной row при stackInput */}
        <div className={clsx(stackInput ? 'flex items-end gap-2' : 'contents')}>

        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={stopTyping}
          onPaste={(e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
              if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (file) {
                  const preview = URL.createObjectURL(file);
                  setAttachedImage({ file, preview });
                }
                return;
              }
            }
          }}
          placeholder={ph}
          disabled={disabled}
          rows={1}
          className="flex-1 bg-transparent text-surface-100 placeholder-surface-500 text-sm resize-none outline-none leading-5 max-h-[200px] py-1"
        />

        {channelId && (
          <>
            <button
              type="button"
              onClick={() => setSilent((v) => !v)}
              disabled={disabled}
              className={clsx(
                'flex-shrink-0 self-end p-1 rounded-lg transition-colors mb-0.5',
                silent ? 'text-yellow-400 bg-surface-700' : 'text-surface-500 hover:text-yellow-400 hover:bg-surface-700',
              )}
              title="Без уведомления"
            >
              <BellOff size={16} />
            </button>
            <button
              type="button"
              onClick={() => setViewOnce((v) => !v)}
              disabled={disabled}
              className={clsx(
                'flex-shrink-0 self-end p-1 rounded-lg transition-colors mb-0.5',
                viewOnce ? 'text-brand-400 bg-surface-700' : 'text-surface-500 hover:text-brand-400 hover:bg-surface-700',
              )}
              title="Просмотр один раз"
            >
              <Eye size={16} />
            </button>
            <button
              type="button"
              onClick={() => setScheduleOpen(true)}
              disabled={disabled}
              className={clsx(
                'flex-shrink-0 self-end p-1 rounded-lg transition-colors mb-0.5',
                scheduledFor ? 'text-brand-400 bg-surface-700' : 'text-surface-500 hover:text-brand-400 hover:bg-surface-700',
              )}
              title="Запланировать"
            >
              <Clock size={16} />
            </button>
          </>
        )}

        {content.trim() || attachedImage ? (
          <button
            onClick={send}
            disabled={(!content.trim() && !attachedImage) || sending || disabled || slowActive}
            title={slowActive ? `Медленный режим: подождите ${slowSecLeft} сек` : undefined}
            className="flex-shrink-0 p-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors relative"
          >
            {slowActive ? (
              <span className="text-white text-xs font-mono px-1 min-w-[24px] inline-block">{slowSecLeft}</span>
            ) : (
              <SwipaSend size={15} className="text-white" />
            )}
          </button>
        ) : (
          <VoiceRecordButton channelId={channelId} recipientId={recipientId} disabled={disabled || slowActive} />
        )}
        </div>
      </div>
      <p className="text-surface-600 text-xs mt-1 px-1 hidden md:block">
        {slowActive ? (
          <span className="text-yellow-500/80">
            ⏱ Медленный режим: ещё {slowSecLeft} сек до следующего сообщения
          </span>
        ) : (
          <>
            <kbd className="font-mono">Enter</kbd> — отправить · <kbd className="font-mono">Shift+Enter</kbd> — новая строка
          </>
        )}
      </p>

      {showEmoji && (
        <EmojiPicker onSelect={insertEmoji} onClose={() => setShowEmoji(false)} />
      )}
      {showStickers && (
        <StickerPicker
          onPick={(st) => {
            // Phase 26 — insert shortcode into input (rendered inline later)
            const name = (st.url.match(/\/([^/]+?)(?:\.[a-z0-9]+)?$/i)?.[1]) ?? st.id;
            setContent((prev) => {
              const sep = prev.length > 0 && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
              return prev + sep + `:${name}: `;
            });
            const el = textareaRef.current;
            if (el) {
              setTimeout(() => {
                el.focus();
                const len = el.value.length;
                el.setSelectionRange(len, len);
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 200) + 'px';
              }, 0);
            }
            setShowStickers(false);
          }}
          onClose={() => setShowStickers(false)}
        />
      )}
      {scheduleOpen && (
        <ScheduleModal
          onClose={() => setScheduleOpen(false)}
          onConfirm={(iso) => { setScheduledFor(iso); setScheduleOpen(false); }}
        />
      )}
    </div>
  );
}

function VoiceRecordButton({ channelId, recipientId, disabled }: { channelId?: string; recipientId?: string; disabled?: boolean }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number>(0);

  const startRecording = async () => {
    try {
      // Higher-quality voice capture: bump sample rate, prefer mono with the
      // built-in noise suppression chain. The browser may ignore unsupported
      // constraints but never errors out — they're just hints.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 256000 });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setDuration(0);
      timerRef.current = window.setInterval(() => setDuration((d) => d + 1), 1000);
    } catch (err) {
      console.warn('Mic access denied', err);
    }
  };

  const stopAndSend = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorder.onstop = async () => {
      recorder.stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerRef.current);
      setRecording(false);
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      if (blob.size < 1000) return; // too short
      try {
        const file = new File([blob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
        const uploaded = await mediaApi.upload(file, { channelId, recipientId });
        const durationMs = duration * 1000;
        const socket = getSocket();
        if (channelId) {
          socket.emit('message:send', { channelId, content: String(durationMs), type: 'VOICE', mediaFileId: uploaded.id, clientMessageId: newClientMessageId() });
        } else if (recipientId) {
          await messagesApi.sendDM(recipientId, { content: String(durationMs), type: 'VOICE', mediaFileId: uploaded.id });
        }
      } catch (err) {
        console.error('Voice send failed', err);
      }
    };
    recorder.stop();
  };

  const cancelRecording = () => {
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.stream.getTracks().forEach((t) => t.stop());
      recorder.stop();
    }
    clearInterval(timerRef.current);
    setRecording(false);
    chunksRef.current = [];
  };

  if (recording) {
    const m = Math.floor(duration / 60);
    const s = duration % 60;
    return (
      <div className="flex items-center gap-2">
        <button onClick={cancelRecording} className="p-1.5 text-red-400 hover:text-red-300">
          <X size={16} />
        </button>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-red-400 text-xs tabular-nums">{m}:{s.toString().padStart(2, '0')}</span>
        </div>
        <button onClick={stopAndSend} className="p-2 rounded-lg bg-brand-600 hover:bg-brand-500">
          <SwipaSend size={15} className="text-white" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={startRecording}
      disabled={disabled}
      className="flex-shrink-0 p-2 rounded-lg bg-surface-700 hover:bg-surface-600 disabled:opacity-40 transition-colors"
      title="Голосовое сообщение"
    >
      <Mic size={15} className="text-surface-300" />
    </button>
  );
}
